import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Link2, Plus, Trash2, ExternalLink, Linkedin, Github, Globe, Pencil } from 'lucide-react';

const BehanceIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M22 7h-7V5h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14H15.97c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988H0V5.021h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.061-3.207 8.061zM3 11h3.584c2.508 0 2.906-3-.312-3H3v3zm3.391 3H3v3.016h3.341c3.055 0 2.868-3.016.05-3.016z"/>
  </svg>
);

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { ElementType } from 'react';

interface ProfileLink { id: string; link_type: string; label: string | null; url: string; }

interface Props {
  links: ProfileLink[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  editLocked?: boolean;
}

const linkMeta: Record<string, { icon: ElementType; color: string; bg: string }> = {
  linkedin: { icon: Linkedin, color: 'text-[hsl(201,100%,35%)]', bg: 'bg-[hsl(201,100%,95%)]' },
  github: { icon: Github, color: 'text-foreground', bg: 'bg-muted' },
  portfolio: { icon: Globe, color: 'text-seek', bg: 'bg-seek/10' },
  personal: { icon: Globe, color: 'text-seek', bg: 'bg-seek/10' },
  behance: { icon: BehanceIcon, color: 'text-[hsl(240,100%,50%)]', bg: 'bg-[hsl(240,100%,96%)]' },
  other: { icon: Link2, color: 'text-muted-foreground', bg: 'bg-muted' },
};

function prettifyUrl(url: string) {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

function linkTypeLabel(type: string) {
  const labels: Record<string, string> = {
    linkedin: 'LinkedIn',
    github: 'GitHub',
    portfolio: 'Portfolio',
    personal: 'Personal Site',
    behance: 'Behance',
    other: 'Link',
  };
  return labels[type] ?? 'Link';
}

export function LinksSection({ links, profileId, onAdd, onUpdate, onDelete, saving, editLocked }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ link_type: 'linkedin', label: '', url: '' });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const openNew = () => { setEditId(null); setForm({ link_type: 'linkedin', label: '', url: '' }); setOpen(true); };
  const openEdit = (l: ProfileLink) => {
    setEditId(l.id);
    setForm({ link_type: l.link_type, label: l.label ?? '', url: l.url });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.url.trim()) return;
    if (editId) {
      onUpdate(editId, { ...form, label: form.label || null, candidate_profile_id: profileId });
    } else {
      onAdd({ ...form, label: form.label || null, candidate_profile_id: profileId });
    }
    setOpen(false);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-display">
            <Link2 className="w-5 h-5 text-seek" />
            {t('seek.profile.links')}
          </h3>
          <Button variant="ghost" size="sm" onClick={openNew} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
            <Plus className="w-4 h-4" /> {t('seek.profile.add')}
          </Button>
        </div>

        {links.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-seek/10 flex items-center justify-center mb-3">
              <Link2 className="w-5 h-5 text-seek" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{t('seek.profile.noLinks')}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">{t('seek.profile.addLinksHint')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {links.map((l) => {
              const meta = linkMeta[l.link_type] ?? linkMeta.other;
              const Icon = meta.icon;
              return (
                <a
                  key={l.id}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3.5 p-3 rounded-xl border border-border bg-card hover:border-seek/40 hover:shadow-sm transition-all duration-200"
                  dir="ltr"
                >
                  <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0', meta.bg)}>
                    <Icon className={cn('w-4 h-4', meta.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {l.label || linkTypeLabel(l.link_type)}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {prettifyUrl(l.url)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={editLocked}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(l); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      disabled={editLocked}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget({ id: l.id, name: l.label || l.url }); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editId ? t('seek.profile.editLink') : t('seek.profile.addLink')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('seek.profile.type')}</Label>
              <Select value={form.link_type} onValueChange={(v) => setForm(p => ({ ...p, link_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="github">GitHub</SelectItem>
                  <SelectItem value="portfolio">{t('seek.profile.portfolio')}</SelectItem>
                  <SelectItem value="behance">Behance</SelectItem>
                  <SelectItem value="personal">{t('seek.profile.personalSite')}</SelectItem>
                  <SelectItem value="other">{t('seek.profile.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('seek.profile.labelOptional')}</Label><Input value={form.label} onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))} placeholder={t('seek.profile.labelPlaceholder')} /></div>
            <div><Label>{t('seek.profile.url')} *</Label><Input value={form.url} onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..." dir="ltr" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!form.url.trim() || saving} className="bg-seek text-white hover:bg-seek/90">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.profile.deleteLink')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.profile.deleteLinkConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
